// Ops WORK QUEUE — the default ops landing. One live view of everything a
// human must act on NOW: compliance checks pending review, empty-leg holds
// about to expire, waiting_human agent tasks, bookings stuck in 'hold', and
// support tickets past their SLA. Polls /api/ops/queue every 5s (react-query).
// OPS-ONLY surface: stale-hold rows carry margin (route is money-role gated).
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Inbox, ShieldCheck, Timer, UserCheck, Briefcase, LifeBuoy, Check } from "lucide-react";

import MockBadge from "@/components/MockBadge";
import StatusPill from "@/components/StatusPill";
import { ops } from "@/lib/api";
import { fmtTime, fmtUSD, shortId } from "@/lib/format";

const SECTIONS = [
  { key: "compliance_pending", label: "Compliance · pending review", icon: ShieldCheck, href: "/compliance" },
  { key: "expiring_holds", label: "Holds · expiring soon", icon: Timer, href: "/empty-legs" },
  { key: "waiting_human_tasks", label: "Agent tasks · waiting on a human", icon: UserCheck, href: "/hermes" },
  { key: "stale_hold_bookings", label: "Bookings · stale holds", icon: Briefcase, href: "/bookings" },
  { key: "sla_due_tickets", label: "Support · SLA due", icon: LifeBuoy, href: "/support" },
];

export default function Queue() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["ops-queue"],
    queryFn: ops.queue,
    refetchInterval: 5000,
    retry: false,
  });

  const resolveTask = async (id) => {
    try {
      await ops.resolveAgentTask(id, "succeeded", "resolved from work queue");
      toast.success("Task resolved");
      qc.invalidateQueries({ queryKey: ["ops-queue"] });
      qc.invalidateQueries({ queryKey: ["ops-queue-badge"] });
    } catch {
      toast.error("Failed to resolve task");
    }
  };

  const counts = data?.counts || {};
  const items = data?.items || {};

  return (
    <div data-testid="page-queue" className="p-6 md:p-10">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
        <div className="min-w-0">
          <div className="pj-label mb-2">· Command Center · Act-now queue</div>
          <h1 className="font-display text-[32px] md:text-[40px] leading-[1.02]">Work Queue</h1>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span data-testid="queue-total" className="mono text-[11px] uppercase tracking-[0.14em] text-[rgb(var(--dim))]">
            {data?.total ?? 0} open items
          </span>
          <MockBadge />
        </div>
      </div>

      {error && (
        <div className="pj-card p-6 mono text-[12px] text-[rgb(196,52,44)]" data-testid="queue-error">
          Could not load the work queue (money roles only: admin / ops / finance).
        </div>
      )}
      {isLoading && !data && (
        <div className="pj-card p-6 mono text-[12px] text-[rgb(var(--dim))]">Loading queue…</div>
      )}

      {data && (
        <div className="space-y-6">
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            const rows = items[s.key] || [];
            return (
              <section key={s.key} className="pj-card" data-testid={`queue-section-${s.key}`}>
                <div className="flex items-center justify-between p-4" style={{ borderBottom: "1px solid rgb(var(--ln))" }}>
                  <h2 className="font-display text-[18px] flex items-center gap-2">
                    <Icon size={14} className="text-[rgb(var(--dim))]" /> {s.label}
                  </h2>
                  <div className="flex items-center gap-3">
                    <span className="mono text-[10px] uppercase tracking-[0.14em] text-[rgb(var(--dim))]" data-testid={`queue-count-${s.key}`}>
                      {counts[s.key] ?? 0}
                    </span>
                    <a href={s.href} className="mono text-[10px] uppercase tracking-[0.14em] text-[rgb(var(--dim))] hover:text-[rgb(var(--fg))]">
                      Open →
                    </a>
                  </div>
                </div>

                {rows.length === 0 && (
                  <div className="p-5 mono text-[11px] text-[rgb(var(--muted-2))]">
                    <Inbox size={12} className="inline mr-2" /> Nothing waiting.
                  </div>
                )}

                <div className="divide-y divide-[rgb(var(--ln))]">
                  {rows.map((r) => (
                    <div
                      key={r.id}
                      data-testid={`queue-row-${r.id}`}
                      className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
                    >
                      <div className="min-w-0 mono text-[12px]">
                        {s.key === "compliance_pending" && (
                          <>
                            <span className="uppercase text-[10px] tracking-[0.14em] text-[rgb(var(--dim))]">{r.check_type}</span>
                            {" · "}{r.entity_type} · {shortId(r.entity_id)} ·{" "}
                            <span style={{ color: r.severity === "high" ? "rgb(196,52,44)" : "rgb(176,116,18)" }}>{r.severity}</span>
                          </>
                        )}
                        {s.key === "expiring_holds" && (
                          <>
                            Hold {shortId(r.id)} · expires {fmtTime(r.expires_at)}
                            {r.booking_id ? <> · booking {shortId(r.booking_id)}</> : null}
                          </>
                        )}
                        {s.key === "waiting_human_tasks" && (
                          <>
                            <span className="uppercase text-[10px] tracking-[0.14em] text-[rgb(var(--dim))]">{r.agent}</span>
                            {" · "}{shortId(r.id)} · since {fmtTime(r.created_at)}
                          </>
                        )}
                        {s.key === "stale_hold_bookings" && (
                          <>
                            {shortId(r.id)} · {fmtUSD(r.client_total_usd)} ·{" "}
                            <span className="text-[rgb(var(--dim))]">Margin {fmtUSD(r.margin_usd)}</span> · held since {fmtTime(r.created_at)}
                          </>
                        )}
                        {s.key === "sla_due_tickets" && (
                          <>
                            <span className="truncate">{r.subject || shortId(r.id)}</span> · due {fmtTime(r.sla_due_at)}
                          </>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {(r.state || r.status || r.result) && (
                          <StatusPill state={r.state || r.status || r.result} />
                        )}
                        {s.key === "waiting_human_tasks" && (
                          <button
                            type="button"
                            data-testid={`btn-resolve-task-${r.id}`}
                            onClick={() => resolveTask(r.id)}
                            className="mono inline-flex items-center gap-1 px-2 py-1.5 text-[10px] uppercase tracking-[0.12em]"
                            style={{ background: "linear-gradient(90deg, rgb(169,130,28), rgb(158,92,44))", color: "rgb(252,249,243)" }}
                          >
                            <Check size={11} /> Resolve
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
